import React, { useState, useMemo, useEffect } from "react";
import { Search, Plus, Loader2 } from "lucide-react";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { UserAvatar } from "@/modern/components/user-avatar";
import { useEntityContactContext } from "@/react/components/entityViews/contacts/components/context/EntityContactContext";
import { cn } from "@/modern/utils";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { useDebounce } from "@/react/hooks/useDebounce";

interface AssignContactEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateContact: () => void;
}

const CONTACTS_PER_PAGE = 20;

export const AssignContactEditor: React.FC<AssignContactEditorProps> = ({ open, onOpenChange, onCreateContact }) => {
  const { assignContacts, loading, getAvailableContacts } = useEntityContactContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [displayedCount, setDisplayedCount] = useState(CONTACTS_PER_PAGE);

  // Debounce search term to avoid performance issues with large datasets
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelectedIds([]);
      setDisplayedCount(CONTACTS_PER_PAGE);
    }
  }, [open]);

  // Get available contacts (not already assigned)
  const availableContacts = useMemo(() => getAvailableContacts(), [getAvailableContacts]);

  // Filter contacts based on debounced search term
  const filteredContacts = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return availableContacts;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return availableContacts.filter(
      (contact) => contact.name?.toLowerCase().includes(searchLower) || contact.phone?.toLowerCase().includes(searchLower) || contact.email?.toLowerCase().includes(searchLower)
    );
  }, [availableContacts, debouncedSearchTerm]);

  // Determine which contacts to display
  const contactsToDisplay = useMemo(() => {
    // If searching, show all filtered results
    if (debouncedSearchTerm.trim()) {
      return filteredContacts;
    }
    // If not searching, show paginated results
    return availableContacts.slice(0, displayedCount);
  }, [debouncedSearchTerm, filteredContacts, availableContacts, displayedCount]);

  // Reset displayed count when search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      // When searching, we don't need pagination
      setDisplayedCount(CONTACTS_PER_PAGE);
    }
  }, [debouncedSearchTerm]);

  // Infinite scroll: load more contacts
  const loadMoreContacts = () => {
    setDisplayedCount((prev) => prev + CONTACTS_PER_PAGE);
  };

  // Check if there are more contacts to load (only relevant when not searching)
  const hasMoreContacts = !debouncedSearchTerm.trim() && displayedCount < availableContacts.length;

  // Check if we're currently searching (for loading state)
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Toggle contact selection
  const toggleSelection = (contactId: string | number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return [...prev, contactId];
      } else {
        return prev.filter((id) => id !== contactId);
      }
    });
  };

  // Handle assign action
  const handleAssign = async () => {
    try {
      await assignContacts(selectedIds);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to assign contacts:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[--swivu-bryntum-z-index] max-w-lg p-0 flex flex-col">
        <DialogHeader className="px-6 py-5">
          <DialogTitle className="flex items-center gap-2 font-normal">Assign Contacts</DialogTitle>
          <DialogDescription className="pt-1.5">
            Select contacts to assign to this entity
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col px-6 pb-5">
          {/* Search Input */}
          <div className="relative py-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 bg-background border-input focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Contact List */}
          <ScrollArea className="h-[400px] mb-4">
            {contactsToDisplay.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                {isSearching ? (
                  <>
                    <Loader2 className="h-10 w-10 text-muted-foreground/50 mb-3 animate-spin" />
                    <p className="text-sm text-muted-foreground">Searching contacts...</p>
                  </>
                ) : (
                  <>
                    <Search className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {debouncedSearchTerm ? "No contacts found matching your search" : "No available contacts to assign"}
                    </p>
                    {debouncedSearchTerm && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Try a different search term
                      </p>
                    )}
                    {!debouncedSearchTerm && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          onOpenChange(false);
                          onCreateContact();
                        }}
                        className="gap-2 mt-4"
                      >
                        <Plus className="h-4 w-4" />
                        Create New Contact
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <InfiniteScroll
                hasMore={hasMoreContacts}
                isLoading={false}
                next={loadMoreContacts}
                threshold={0.8}
              >
                {contactsToDisplay.map((contact, index) => {
                  const isSelected = selectedIds.includes(contact.id);

                  return (
                    <div
                      key={contact.id}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors rounded-md",
                        "hover:bg-accent",
                        isSelected && "bg-accent",
                        index < contactsToDisplay.length - 1 && "mb-1"
                      )}
                      onClick={() => toggleSelection(contact.id, !isSelected)}
                    >
                      <UserAvatar
                        user={{
                          id: contact.id as number,
                          name: contact?.name || "N/A",
                          profileImageURL: undefined,
                          archived: false,
                        }}
                        size="default"
                        showName={true}
                        secondaryText={contact.email}
                        className="max-w-[350px]"
                      />

                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleSelection(contact.id, checked as boolean)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0"
                      />
                    </div>
                  );
                })}
                {hasMoreContacts && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </InfiniteScroll>
            )}
          </ScrollArea>

          {/* Selection Counter & Action Buttons */}
          <div className="flex items-center justify-between gap-3 pt-5 border-t">
            <span className={cn(
              "text-sm",
              selectedIds.length > 0 ? "text-foreground font-medium" : "text-muted-foreground"
            )}>
              {selectedIds.length} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} size="sm" disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleAssign} size="sm" disabled={selectedIds.length === 0 || loading}>
                {loading ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
